import {ArrowRight, Banknote, Coins, Gem, Landmark, PiggyBank, Scale, ShieldCheck, Sparkles, TrendingUp, Wallet} from 'lucide-react'
import { Link } from 'react-router-dom'
import AdSlot from '@/components/AdSlot'
import AffiliateCard from '@/components/AffiliateCard'
import Seo from '@/components/Seo'
import { softwareApplicationSchema } from '@/lib/schema'

const calculatorGroups = [
  {
    silo: 'Personal Budgeting & Net Worth',
    accent: 'emerald',
    items: [
      { to: '/calculators/budget-50-30-20', icon: PiggyBank, name: '50/30/20 Budget Calculator', desc: 'Zero-based cash flow planning with real-time surplus tracking.' },
      { to: '/calculators/net-worth', icon: Scale, name: 'Net Worth Calculator', desc: 'Track assets vs liabilities and benchmark against your age group.' },
      { to: '/calculators/emergency-fund', icon: ShieldCheck, name: 'Emergency Fund Calculator', desc: 'Find your ideal 3-6 month safety net buffer target.' },
    ],
  },
  {
    silo: 'Loans, Credit & Debt Payoff',
    accent: 'cyan',
    items: [
      { to: '/calculators/mint-loan', icon: Landmark, name: 'Mint Loan & Amortization', desc: 'Full amortization schedule with extra-payment savings.' },
      { to: '/calculators/credit-card-payoff', icon: Wallet, name: 'Credit Card Payoff', desc: 'Snowball vs avalanche payoff timeline comparison.' },
      { to: '/calculators/mortgage-payoff', icon: Banknote, name: 'Mortgage Payoff', desc: 'See how extra principal payments shorten your mortgage.' },
    ],
  },
  {
    silo: 'Web3 & Crypto Mint Gas',
    accent: 'teal',
    items: [
      { to: '/calculators/nft-mint-cost', icon: Sparkles, name: 'NFT Mint Cost Estimator', desc: 'Ethereum, Solana & Polygon gas fee breakdown per mint.' },
      { to: '/calculators/solana-mint-cost', icon: TrendingUp, name: 'Solana Mint Cost', desc: 'Low-fee SPL token minting cost calculator.' },
    ],
  },
  {
    silo: 'Numismatic Coin & Bullion',
    accent: 'amber',
    items: [
      { to: '/calculators/silver-coin-value', icon: Coins, name: 'Silver Coin Value', desc: 'Melt value + numismatic premium for silver coins.' },
      { to: '/calculators/gold-eagle-mint', icon: Gem, name: 'Gold Eagle Mint Value', desc: 'Spot price, purity & grade-adjusted gold coin value.' },
    ],
  },
]

const accentClasses: Record<string, { bg: string, text: string, border: string }> = {
  emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'hover:border-emerald-500/40' },
  cyan: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', border: 'hover:border-cyan-500/40' },
  teal: { bg: 'bg-teal-500/10', text: 'text-teal-400', border: 'hover:border-teal-500/40' },
  amber: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'hover:border-amber-500/40' },
}

function Home() {
  return (
    <>
      <Seo
        title="Mint Calculator - Free Budget, Loan, NFT & Coin Value Calculators"
        description="The complete replacement for Intuit Mint. Free 50/30/20 budget, loan amortization, NFT gas fee, and numismatic coin value calculators with real-time results."
        path="/"
        schema={softwareApplicationSchema(
          'Mint Calculator',
          'A comprehensive free suite of personal finance, loan, NFT gas, and numismatic coin valuation calculators.',
          'https://www.mintcalculator.com',
        )}
      />

      <section className="relative overflow-hidden border-b border-slate-800">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(16,185,129,0.12),transparent_60%)]" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-400">
              <Sparkles className="h-3.5 w-3.5" />
              The free calculator suite for the post-Mint era
            </span>
            <h1 className="mt-5 text-4xl font-bold tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              Every number your money
              {' '}
              <span className="text-emerald-400">actually needs</span>
            </h1>
            <p className="mt-5 text-lg text-slate-400">
              Budgeting, loan amortization, NFT mint gas, and coin melt value — ten precision calculators,
              zero sign-up, instant sub-millisecond results.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/calculators/budget-50-30-20" className="flex items-center gap-2 rounded-lg bg-emerald-500 px-6 py-3 text-sm font-semibold text-slate-950 transition-colors hover:bg-emerald-400">
                Start Budgeting
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/calculators/mint-loan" className="rounded-lg border border-slate-700 px-6 py-3 text-sm font-semibold text-slate-200 transition-colors hover:border-emerald-500/50 hover:text-emerald-400">
                Explore Loan Tools
              </Link>
            </div>
          </div>

          <div className="mx-auto mt-12 grid max-w-3xl grid-cols-2 gap-4 sm:grid-cols-4">
            {[
              { label: 'Calculators', value: '10' },
              { label: 'Keywords Tracked', value: '50+' },
              { label: 'Avg. Calc Time', value: '<1ms' },
              { label: 'Cost', value: 'Free' },
            ].map(stat => (
              <div key={stat.label} className="rounded-xl border border-slate-800 bg-slate-900/50 p-4 text-center">
                <p className="font-mono text-2xl font-bold text-emerald-400">{stat.value}</p>
                <p className="mt-1 text-xs text-slate-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="space-y-14">
          {calculatorGroups.map(group => (
            <div key={group.silo}>
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-50">{group.silo}</h2>
              </div>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {group.items.map((item) => {
                  const Icon = item.icon
                  const accent = accentClasses[group.accent]
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`group rounded-xl border border-slate-800 bg-slate-900/40 p-5 transition-colors ${accent.border}`}
                    >
                      <span className={`flex h-11 w-11 items-center justify-center rounded-lg ${accent.bg} ${accent.text}`}>
                        <Icon className="h-5 w-5" />
                      </span>
                      <p className="mt-4 text-base font-semibold text-slate-100">{item.name}</p>
                      <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{item.desc}</p>
                      <span className={`mt-4 inline-flex items-center gap-1 text-sm font-medium ${accent.text}`}>
                        Calculate now
                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </Link>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-800 bg-slate-900/30 py-4">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <AdSlot variant="inline" />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-50">Trusted Financial Partners</h2>
          <Link to="/about" className="text-sm font-medium text-emerald-400 hover:text-emerald-300">Why we recommend these</Link>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <AffiliateCard icon={PiggyBank} name="High-Yield Savings Accounts" tagline="Earn 4.5%+ APY on your emergency fund" cta="Compare Rates" accent="emerald" />
          <AffiliateCard icon={Landmark} name="SoFi / Earnest Refinancing" tagline="Lower your loan APR in minutes" cta="Check Rate" accent="cyan" />
          <AffiliateCard icon={Wallet} name="Monarch / YNAB" tagline="The budgeting app built for post-Mint life" cta="Try Free" accent="teal" />
          <AffiliateCard icon={ShieldCheck} name="Ledger / Trezor" tagline="Secure your crypto with a hardware wallet" cta="Shop Wallets" accent="amber" />
        </div>
      </section>
    </>
  )
}

export default Home
